import React from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { Search, Clear } from "@mui/icons-material";
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";

const useStyles = createUseStyles({
    searchContainer: {
        display: "flex",
        alignItems: "center",
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        background: "transparent",
        "& .MuiFormControl-root": {
            margin: 0,
            width: "100%",
            background: "transparent",
        },
        "& .MuiOutlinedInput-root": {
            width: "100%",
            maxWidth: "100%",
            borderRadius: 20,
            backgroundColor: "var(--spara-input-bg, #fff)",
            color: "var(--spara-text, inherit)",
            minHeight: 36,
            // Border lives on fieldset only — avoids double outline / red halo
            border: "none",
            transition: "background-color 0.2s ease",
            "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: 20,
                borderWidth: "1px",
                borderColor: "var(--spara-input-border, rgba(0, 0, 0, 0.12))",
                transition: "border-color 0.2s ease",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--spara-border, rgba(0, 0, 0, 0.24))",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: "1px",
                borderColor: "var(--spara-primary, #DE2C48)",
            },
            "& input": {
                color: "var(--spara-text, inherit)",
                backgroundColor: "transparent !important",
                border: "none !important",
                boxShadow: "none",
                padding: "6px 16px !important",
                height: 24,
                width: "100%",
                "&::placeholder": {
                    color: "var(--spara-text-muted, #666)",
                    opacity: 1,
                },
            },
        },
    },
    searchContainerWithActions: {
        "& .MuiOutlinedInput-root": {
            paddingRight: 40,
        },
    },
    searchContainerWithClear: {
        "& .MuiOutlinedInput-root": {
            paddingRight: 72,
        },
    },
    searchButton: {
        position: "absolute",
        right: 4,
        padding: 4,
        minWidth: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1,
    },
    clearButton: {
        position: "absolute",
        right: 4,
        padding: 4,
        minWidth: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "transparent",
        zIndex: 1,
        "&:hover": {
            backgroundColor: "var(--spara-surface-hover, rgba(0, 0, 0, 0.04))",
        },
        "& .MuiSvgIcon-root": {
            color: "var(--spara-text-muted, rgba(0, 0, 0, 0.54))",
        },
    },
    clearButtonWithSearch: {
        right: 36,
    },
});

const SearchBar = ({
    value,
    onChange,
    placeholder,
    showSearchButton = true,
    onSearch,
    onClear,
}) => {
    const classes = useStyles();
    const hasValue = Boolean(value);
    const showClear = hasValue;
    const paddingClass =
        showClear && showSearchButton
            ? classes.searchContainerWithClear
            : showClear || showSearchButton
              ? classes.searchContainerWithActions
              : "";

    const handleClear = () => {
        onChange({ target: { value: "" } });
        if (onClear) {
            onClear();
        }
    };

    return (
        <div className={`${classes.searchContainer} ${paddingClass}`.trim()}>
            <CustomInput
                formControlProps={{
                    fullWidth: true,
                }}
                inputProps={{
                    value,
                    onChange,
                    placeholder,
                    autoFocus: true,
                    onKeyDown: (e) => e.key === "Enter" && onSearch && onSearch(),
                    inputProps: {
                        "aria-label": "Search",
                        style: { padding: "6px 16px" },
                    },
                }}
            />
            {showClear && (
                <Button
                    simple
                    aria-label="clear"
                    justIcon
                    size="sm"
                    className={`${classes.clearButton} ${
                        showSearchButton ? classes.clearButtonWithSearch : ""
                    }`.trim()}
                    onClick={handleClear}
                >
                    <Clear />
                </Button>
            )}
            {showSearchButton && (
                <Button
                    color="rose"
                    aria-label="search"
                    justIcon
                    size="sm"
                    className={classes.searchButton}
                    onClick={onSearch}
                >
                    <Search />
                </Button>
            )}
        </div>
    );
};

SearchBar.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    t: PropTypes.func.isRequired,
    showSearchButton: PropTypes.bool,
    onSearch: PropTypes.func,
    onClear: PropTypes.func,
};

export default withTranslation("", { withRef: true })(SearchBar);
